/** Severity helpers shared by the repos list and the repo Findings tab. */

export const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
export type Severity = (typeof SEVERITY_ORDER)[number];

/** Per-severity counts. UNKNOWN/untriaged severities are not surfaced as columns. */
export type SeverityCounts = Record<Severity, number>;

export const EMPTY_COUNTS: SeverityCounts = {
  CRITICAL: 0,
  HIGH: 0,
  MEDIUM: 0,
  LOW: 0,
};

/** react-bootstrap Badge `bg` variant per severity (warning text needs dark fg). */
export const SEVERITY_BADGE_BG: Record<Severity, string> = {
  CRITICAL: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'secondary',
};

/** Normalize a raw severity string to one of our buckets, or null if unrecognized. */
export function normalizeSeverity(raw: unknown): Severity | null {
  const s = String(raw ?? '').toUpperCase();
  return (SEVERITY_ORDER as readonly string[]).includes(s) ? (s as Severity) : null;
}

/** Tally severity counts from a list of {severity} rows. */
export function countBySeverity(
  rows: Array<{ severity?: string }>,
): SeverityCounts {
  const out: SeverityCounts = { ...EMPTY_COUNTS };
  for (const r of rows) {
    const sev = normalizeSeverity(r.severity);
    if (sev) out[sev] += 1;
  }
  return out;
}

/**
 * A single weighted score for sorting repos by "needs attention", so a repo with one
 * CRITICAL ranks above one with many LOWs. Weights are order-of-magnitude so lower
 * severities never outrank a single higher one in realistic counts.
 */
export function severityScore(c: SeverityCounts): number {
  return c.CRITICAL * 1_000_000 + c.HIGH * 10_000 + c.MEDIUM * 100 + c.LOW;
}

export function totalCount(c: SeverityCounts): number {
  return c.CRITICAL + c.HIGH + c.MEDIUM + c.LOW;
}
